const EXPIRABLE_AGREEMENT_STATUSES = new Set([
  "sent",
  "open",
  "opened",
  "pending",
]);

/** "YYYY-MM-DD HH:mm:ss" with no zone marker, as several APIs still return. */
const NAIVE_UTC_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;

/**
 * The API sends UTC timestamps, but a zone-less string is parsed as *local*
 * time by Date, which moved every expiry by the viewer's UTC offset — enough to
 * mark a still-live agreement Expired. Pin such strings to UTC; values that
 * already carry a zone (ISO with Z or an offset) are left alone.
 */
const toUtcTimestamp = (value: string | Date): number => {
  if (typeof value === "string" && NAIVE_UTC_TIMESTAMP.test(value.trim())) {
    return new Date(`${value.trim().replace(" ", "T")}Z`).getTime();
  }
  return new Date(value).getTime();
};

/**
 * Treat an agreement offer as expired once its expiry timestamp has passed,
 * even when the agreement-expiration job has not updated the persisted status.
 * Terminal agreement statuses must always remain authoritative.
 */
export const getEffectiveAgreementStatus = (
  status: string | null | undefined,
  expiryDate: string | Date | null | undefined,
  now: string | Date = new Date()
): string => {
  const originalStatus = status || "";
  const normalizedStatus = originalStatus.trim().toLowerCase();

  if (!EXPIRABLE_AGREEMENT_STATUSES.has(normalizedStatus) || !expiryDate) {
    return originalStatus;
  }

  const expiryTimestamp = toUtcTimestamp(expiryDate);
  const currentTimestamp = toUtcTimestamp(now);

  if (
    Number.isNaN(expiryTimestamp) ||
    Number.isNaN(currentTimestamp) ||
    currentTimestamp <= expiryTimestamp
  ) {
    return originalStatus;
  }

  return "Expired";
};
